import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, FileText, CreditCard, Activity, MessageSquare, Download, CheckCircle2, ChevronRight, User, Send, Mail, Phone, MapPin } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsPortal({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  const [activeTab, setActiveTab] = useState<'profile' | 'contract' | 'messaging' | 'billing'>('profile');
  const [message, setMessage] = useState('');
  return <div className="min-h-screen bg-[#F3F6F9] font-sans" data-id="element-1869">
      <SimonsHeader activePage="portal" setActivePage={setActivePage} data-id="element-1870" />

      <main className="max-w-7xl mx-auto p-8" data-id="element-1871">
        <div className="mb-10" data-id="element-1872">
          <h1 className="text-3xl font-black text-[#002366] uppercase tracking-tight" data-id="element-1873">
            Secure Partner Portal
          </h1>
          <p className="text-slate-500 font-medium" data-id="element-1874">
            Manage your profile, insurance contracts, and communication in one
            secure environment.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1875">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-1876">
            <div data-id="element-1877">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-1878">
                Account Status
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-1879">
                Verified Partner
              </p>
            </div>
            <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center text-teal-600" data-id="element-1880">
              <CheckCircle2 size={20} data-id="element-1881" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-1882">
            <div data-id="element-1883">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-1884">
                Active Contracts
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-1885">2 Policies</p>
            </div>
            <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-[#002366]" data-id="element-1886">
              <Shield size={20} data-id="element-1887" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between" data-id="element-1888">
            <div data-id="element-1889">
              <p className="text-[10px] font-black uppercase text-slate-400 mb-1" data-id="element-1890">
                Unread Messages
              </p>
              <p className="text-lg font-black text-[#002366]" data-id="element-1891">1 New</p>
            </div>
            <div className="w-10 h-10 bg-[#FFD700]/10 rounded-full flex items-center justify-center text-[#002366]" data-id="element-1892">
              <MessageSquare size={20} data-id="element-1893" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8" data-id="element-1894">
          {/* Sidebar Tabs */}
          <div className="space-y-2" data-id="element-1895">
            {[{
            id: 'profile',
            label: 'My Profile',
            icon: User
          }, {
            id: 'contract',
            label: 'Insurance Contract',
            icon: FileText
          }, {
            id: 'messaging',
            label: 'Message Agent',
            icon: MessageSquare
          }, {
            id: 'billing',
            label: 'Manage Billing',
            icon: CreditCard
          }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`w-full flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-sm transition-all ${activeTab === tab.id ? 'bg-[#002366] text-white shadow-lg' : 'bg-white text-slate-400 hover:bg-slate-50'}`} data-id="element-1896">
                <tab.icon size={18} data-id="element-1897" />
                {tab.label}
              </button>)}
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 min-h-[600px]" data-id="element-1898">
            {activeTab === 'profile' && <div className="space-y-8" data-id="element-1899">
                <div className="flex justify-between items-center" data-id="element-1900">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-1901">
                    My Profile
                  </h3>
                  <button className="px-4 py-2 bg-slate-100 rounded-lg text-xs font-black uppercase text-[#002366] hover:bg-slate-200" data-id="element-1902">
                    Edit Profile
                  </button>
                </div>
                <div className="flex flex-col md:flex-row gap-12 items-start" data-id="element-1903">
                  <div className="w-32 h-32 rounded-3xl bg-slate-100 overflow-hidden border-4 border-white shadow-lg" data-id="element-1904">
                    <img src="https://i.pravatar.cc/150?img=33" alt="Profile" className="w-full h-full object-cover" data-id="element-1905" />
                  </div>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1906">
                    <div className="space-y-1" data-id="element-1907">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-1908">
                        Full Name
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-1909">
                        John Investor
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-1910">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-1911">
                        Email Address
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-1912">
                        john.investor@email.com
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-1913">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-1914">
                        Phone Number
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-1915">
                        +1 (555) 123-4567
                      </p>
                    </div>
                    <div className="space-y-1" data-id="element-1916">
                      <p className="text-[10px] font-black uppercase text-slate-400" data-id="element-1917">
                        Mailing Address
                      </p>
                      <p className="text-lg font-bold text-[#002366]" data-id="element-1918">
                        123 Strategic Way, Suite 100, NY 10001
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-8 border-t border-slate-100" data-id="element-1919">
                  <h4 className="text-sm font-black uppercase text-slate-400 mb-4" data-id="element-1920">
                    Security Settings
                  </h4>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-id="element-1921">
                    <div className="flex items-center gap-3" data-id="element-1922">
                      <Shield size={20} className="text-[#002366]" data-id="element-1923" />
                      <span className="text-sm font-bold" data-id="element-1924">
                        Two-Factor Authentication
                      </span>
                    </div>
                    <div className="w-10 h-5 bg-teal-500 rounded-full relative" data-id="element-1925">
                      <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-1926"></div>
                    </div>
                  </div>
                </div>
              </div>}

            {activeTab === 'contract' && <div className="space-y-8" data-id="element-1927">
                <div className="flex justify-between items-center" data-id="element-1928">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-1929">
                    Insurance Contracts
                  </h3>
                  <button className="px-4 py-2 bg-[#002366] text-white rounded-lg text-xs font-black uppercase tracking-widest hover:bg-[#001a4d]" data-id="element-1930">
                    Add New Policy
                  </button>
                </div>
                <div className="space-y-6" data-id="element-1931">
                  {[{
                id: 'SA-98234-COMM',
                type: 'Commercial Portfolio',
                status: 'Active',
                renewal: 'Jan 01, 2027'
              }, {
                id: 'SA-98234-AUTO',
                type: 'Commercial Auto Fleet',
                status: 'Active',
                renewal: 'Dec 15, 2026'
              }].map(policy => <div key={policy.id} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col md:flex-row justify-between gap-6" data-id="element-1932">
                      <div className="flex items-start gap-4" data-id="element-1933">
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#002366] shadow-sm" data-id="element-1934">
                          <FileText size={24} data-id="element-1935" />
                        </div>
                        <div data-id="element-1936">
                          <p className="text-lg font-black text-[#002366]" data-id="element-1937">
                            {policy.type}
                          </p>
                          <p className="text-sm text-slate-500" data-id="element-1938">
                            Policy ID: {policy.id}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col md:items-end justify-center gap-2" data-id="element-1939">
                        <span className="px-3 py-1 bg-teal-50 text-teal-600 text-[10px] font-black uppercase rounded-full border border-teal-100 w-fit" data-id="element-1940">
                          {policy.status}
                        </span>
                        <p className="text-xs text-slate-400 font-bold" data-id="element-1941">
                          Renewal: {policy.renewal}
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1942">
                        <button className="p-3 bg-white text-[#002366] rounded-xl border border-slate-200 hover:bg-slate-50 transition-all" data-id="element-1943">
                          <Download size={18} data-id="element-1944" />
                        </button>
                        <button className="px-4 py-3 bg-[#002366] text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-[#001a4d] transition-all" data-id="element-1945">
                          View Details
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}

            {activeTab === 'messaging' && <div className="space-y-8 h-full flex flex-col" data-id="element-1946">
                <div className="flex justify-between items-center" data-id="element-1947">
                  <h3 className="text-2xl font-black text-[#002366]" data-id="element-1948">
                    Message Agent
                  </h3>
                  <div className="flex items-center gap-3" data-id="element-1949">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white shadow-sm" data-id="element-1950">
                      <img src="https://i.pravatar.cc/100?img=12" alt="Agent" className="w-full h-full object-cover" data-id="element-1951" />
                    </div>
                    <div data-id="element-1952">
                      <p className="text-xs font-black text-[#002366]" data-id="element-1953">
                        Simons Acquah
                      </p>
                      <p className="text-[10px] text-teal-600 font-bold uppercase" data-id="element-1954">
                        Online
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-slate-50 rounded-3xl p-6 overflow-y-auto space-y-4 min-h-[300px]" data-id="element-1955">
                  <div className="flex justify-start" data-id="element-1956">
                    <div className="max-w-[80%] bg-white p-4 rounded-2xl rounded-tl-none shadow-sm border border-slate-100" data-id="element-1957">
                      <p className="text-sm text-[#002366] font-medium" data-id="element-1958">
                        Hello John! I've reviewed your recent policy update.
                        Everything looks strategic. Do you have any other
                        questions?
                      </p>
                      <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase" data-id="element-1959">
                        10:45 AM
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end" data-id="element-1960">
                    <div className="max-w-[80%] bg-[#002366] p-4 rounded-2xl rounded-tr-none shadow-lg text-white" data-id="element-1961">
                      <p className="text-sm font-medium" data-id="element-1962">
                        Thanks Simons! I'm looking to add another property to
                        the portfolio next month. What's the best way to start?
                      </p>
                      <p className="text-[10px] text-white/40 mt-2 font-bold uppercase text-right" data-id="element-1963">
                        11:02 AM
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-1964">
                  <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Type your message to Simons..." className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 pr-16 focus:outline-none focus:border-[#002366] min-h-[100px] resize-none" data-id="element-1965" />
                  <button className="absolute bottom-4 right-4 p-3 bg-[#002366] text-white rounded-xl hover:bg-[#001a4d] transition-all shadow-lg" data-id="element-1966">
                    <Send size={20} data-id="element-1967" />
                  </button>
                </div>
              </div>}

            {activeTab === 'billing' && <div className="space-y-8" data-id="element-1968">
                <h3 className="text-2xl font-black text-[#002366]" data-id="element-1969">
                  Manage Billing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1970">
                  <div className="p-8 bg-[#002366] text-white rounded-[32px] shadow-2xl relative overflow-hidden" data-id="element-1971">
                    <div className="relative z-10" data-id="element-1972">
                      <div className="flex justify-between items-start mb-12" data-id="element-1973">
                        <CreditCard size={32} className="text-[#FFD700]" data-id="element-1974" />
                        <span className="text-xs font-black uppercase tracking-widest text-white/40" data-id="element-1975">
                          Primary Card
                        </span>
                      </div>
                      <p className="text-xl font-bold tracking-[0.2em] mb-8" data-id="element-1976">
                        •••• •••• •••• 4242
                      </p>
                      <div className="flex justify-between items-end" data-id="element-1977">
                        <div data-id="element-1978">
                          <p className="text-[10px] font-black uppercase text-white/40 mb-1" data-id="element-1979">
                            Card Holder
                          </p>
                          <p className="text-sm font-bold" data-id="element-1980">JOHN INVESTOR</p>
                        </div>
                        <div data-id="element-1981">
                          <p className="text-[10px] font-black uppercase text-white/40 mb-1" data-id="element-1982">
                            Expires
                          </p>
                          <p className="text-sm font-bold" data-id="element-1983">12/28</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full blur-3xl" data-id="element-1984"></div>
                  </div>

                  <div className="space-y-4" data-id="element-1985">
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100" data-id="element-1986">
                      <p className="text-[10px] font-black uppercase text-slate-400 mb-2" data-id="element-1987">
                        Billing Address
                      </p>
                      <p className="text-sm font-bold text-[#002366]" data-id="element-1988">
                        123 Strategic Way, Suite 100, New York, NY 10001
                      </p>
                      <button className="mt-4 text-xs font-black text-[#002366] hover:underline" data-id="element-1989">
                        Update Address
                      </button>
                    </div>
                    <button className="w-full py-4 border-2 border-dashed border-slate-200 rounded-2xl text-slate-400 font-bold text-sm hover:border-[#002366] hover:text-[#002366] transition-all flex items-center justify-center gap-2" data-id="element-1990">
                      + Add Payment Method
                    </button>
                  </div>
                </div>

                <div className="pt-8 border-t border-slate-100" data-id="element-1991">
                  <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6" data-id="element-1992">
                    Recent Transactions
                  </h4>
                  <div className="space-y-4" data-id="element-1993">
                    {[{
                  date: 'May 15, 2026',
                  amount: '$450.00',
                  status: 'Paid'
                }, {
                  date: 'Apr 15, 2026',
                  amount: '$450.00',
                  status: 'Paid'
                }].map((t, i) => <div key={i} className="flex justify-between items-center py-3 border-b border-slate-50 last:border-0" data-id="element-1994">
                        <div data-id="element-1995">
                          <p className="text-sm font-bold text-[#002366]" data-id="element-1996">
                            {t.date}
                          </p>
                          <p className="text-[10px] text-slate-400 font-bold uppercase" data-id="element-1997">
                            Monthly Premium
                          </p>
                        </div>
                        <div className="text-right" data-id="element-1998">
                          <p className="text-sm font-black text-[#002366]" data-id="element-1999">
                            {t.amount}
                          </p>
                          <p className="text-[10px] font-black text-teal-600 uppercase" data-id="element-2000">
                            {t.status}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </main>

      <SimonsFooter data-id="element-2001" />
    </div>;
}